// FarmFreshAgricultureLayout.js - Fixed with Story Modal
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-teal-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:border-teal-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullstory") || field.toLowerCase().includes("story");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function FarmFreshAgricultureLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#0D9488"; // Teal
  const secondary = T.secondaryColor || "#14B8A6"; // Light Teal
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeSection, setActiveSection] = useState("home");
  const [visibleItems, setVisibleItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Farm hero images - DIRECTLY FROM CONFIG
  const farmHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/698aeaf4-8dfb-45b8-b7de-488a99d8331c_farm1.jpg",
    },
    {
      field: "farmHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/08da18f2-521e-4c60-a668-929418d6bc02_mm.jpg",
    },
    {
      field: "farmHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/9d1465f7-1ee0-4096-844f-0fda57a22401_farm3.jpg",
    },
    {
      field: "farmHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7f8791f5-2abc-4804-b02b-e11bea3381a3_farm4.avif",
    },
  ];

  // Farm Products - Added imageField
  const farmProducts = [
    {
      id: 1,
      nameKey: "product1_name",
      name: "Organic Tomatoes",
      priceKey: "product1_price",
      price: "₹299/kg",
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e6bb8fc4-3328-4bea-bf2f-136053fc043a_organic_tomato.jpeg",
      badgeKey: "product1_badge",
      badge: "Fresh",
      badgeColor: "#0D9488",
      categoryKey: "product1_category",
      category: "Vegetables"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Fresh Greens",
      priceKey: "product2_price",
      price: "₹199/bunch",
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/01720359-0459-4efc-a8a0-1393e889b592_Fresh_Greens.jpg",
      badgeKey: "product2_badge",
      badge: "Organic",
      badgeColor: "#14B8A6",
      categoryKey: "product2_category",
      category: "Leafy"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Raw Honey",
      priceKey: "product3_price",
      price: "₹449/500ml",
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d5f46d6d-0fac-420d-84e3-20c8c99cf28f_Raw_Honey.jpg",
      badgeKey: "product3_badge",
      badge: "Pure",
      badgeColor: "#D97706",
      categoryKey: "product3_category",
      category: "Honey"
    },
    {
      id: 4,
      nameKey: "product4_name",
      name: "Farm Fresh Milk",
      priceKey: "product4_price",
      price: "₹149/L",
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a1738869-3a04-4dd8-825c-5b233b9fb0ac_Farm_Fresh_Milk.jpg",
      badgeKey: "product4_badge",
      badge: "Fresh",
      badgeColor: "#0D9488",
      categoryKey: "product4_category",
      category: "Dairy"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Artisan Cheese",
      priceKey: "product5_price",
      price: "₹399/200g",
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/15e45934-53cb-4ed8-a6aa-e9b93a0e2c50_Artisan_Cheese.jpg",
      badgeKey: "product5_badge",
      badge: "Artisan",
      badgeColor: "#D97706",
      categoryKey: "product5_category",
      category: "Dairy"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Organic Eggs",
      priceKey: "product6_price",
      price: "₹299/12pk",
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c40e55a7-1c15-405e-bec3-ba71bb4c61da_Organic_Eggs.jpg",
      badgeKey: "product6_badge",
      badge: "Free Range",
      badgeColor: "#14B8A6",
      categoryKey: "product6_category",
      category: "Poultry"
    },
    {
      id: 7,
      nameKey: "product7_name",
      name: "Heritage Flour",
      priceKey: "product7_price",
      price: "₹499/2kg",
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/55377162-cb16-4f29-a14e-6d00c6ab949d_Heritage_Flour.jpg",
      badgeKey: "product7_badge",
      badge: "Heritage",
      badgeColor: "#D97706",
      categoryKey: "product7_category",
      category: "Grains"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Herb Garden Kit",
      priceKey: "product8_price",
      price: "₹599/kit",
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8e36c889-7174-4e86-aa7b-dbca7456a9f2_Herb_Garden_Kit.jpg",
      badgeKey: "product8_badge",
      badge: "Garden",
      badgeColor: "#0D9488",
      categoryKey: "product8_category",
      category: "Garden"
    }
  ];

  // Farm Features - Added editable fields
  const farmFeatures = [
    { icon: "🌱", titleKey: "feature1_title", title: "100% Organic", descKey: "feature1_desc", desc: "Grown without chemicals" },
    { icon: "🐄", titleKey: "feature2_title", title: "Grass-Fed", descKey: "feature2_desc", desc: "Happy, healthy animals" },
    { icon: "🌾", titleKey: "feature3_title", title: "Heirloom Seeds", descKey: "feature3_desc", desc: "Preserving biodiversity" },
    { icon: "🚜", titleKey: "feature4_title", title: "Family Farm", descKey: "feature4_desc", desc: "Three generations" },
    { icon: "🌿", titleKey: "feature5_title", title: "Sustainable", descKey: "feature5_desc", desc: "Eco-friendly practices" },
    { icon: "🍯", titleKey: "feature6_title", title: "Raw & Pure", descKey: "feature6_desc", desc: "Unprocessed goodness" },
  ];

  // Farm Stories - WITH FULL CONTENT - Added imageField
  const farmStories = [
    {
      id: 1,
      titleKey: "story1_title",
      title: "The Art of Organic Farming",
      excerptKey: "story1_excerpt",
      excerpt: "Discover the beauty and benefits of organic farming methods...",
      fullStoryKey: "story1_fullStory",
      fullStory: "Organic farming is more than just a method of agriculture—it's a philosophy that respects the land, the ecosystem, and the community. At Farm Fresh, we've embraced organic practices for over 15 years, and the results speak for themselves.\n\nOur journey began with a simple belief: that food should be grown in harmony with nature, not against it. We use natural fertilizers, practice crop rotation, and maintain healthy soil through composting and cover cropping. These methods not only produce healthier, more nutritious food but also protect the environment for future generations.\n\nToday, our farm is a thriving ecosystem where beneficial insects, birds, and wildlife coexist with our crops. We're proud to be part of a growing movement that's redefining what agriculture can be—sustainable, ethical, and delicious.",
      imageField: "story1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b2f90235-7345-47f9-8acc-b86874ad5790_The_Art_of_Organic_Farming.jpg",
      categoryKey: "story1_category",
      category: "Organic",
      authorKey: "story1_author",
      author: "Maria Green",
      dateKey: "story1_date",
      date: "January 15, 2025",
      readTimeKey: "story1_readTime",
      readTime: "5 min read"
    },
    {
      id: 2,
      titleKey: "story2_title",
      title: "Farm-to-Table Movement",
      excerptKey: "story2_excerpt",
      excerpt: "How local food systems are revolutionizing eating...",
      fullStoryKey: "story2_fullStory",
      fullStory: "The farm-to-table movement isn't just a trend—it's a return to the way food was meant to be enjoyed. By connecting directly with local farms, consumers can experience the freshest, most flavorful produce while supporting sustainable agriculture.\n\nAt our farm, we take pride in delivering our harvest directly to local restaurants, markets, and families. This means shorter supply chains, reduced carbon footprint, and food that's picked at the peak of ripeness. The difference in taste is remarkable—our tomatoes burst with flavor, our greens are crisp and vibrant, and our dairy products are rich and creamy.\n\nWhen you choose farm-to-table, you're not just eating well—you're investing in your community, supporting farmers, and helping build a more resilient food system.",
      imageField: "story2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6bd06751-1416-4269-b293-f3c72e6d4254_Farm-to-Table_Movement.jpg",
      categoryKey: "story2_category",
      category: "Food Systems",
      authorKey: "story2_author",
      author: "James Peterson",
      dateKey: "story2_date",
      date: "January 10, 2025",
      readTimeKey: "story2_readTime",
      readTime: "4 min read"
    },
    {
      id: 3,
      titleKey: "story3_title",
      title: "Sustainable Agriculture",
      excerptKey: "story3_excerpt",
      excerpt: "Innovative methods for a greener future...",
      fullStoryKey: "story3_fullStory",
      fullStory: "Sustainability in agriculture is about more than just using organic methods—it's about creating systems that can thrive for generations. At Farm Fresh, we're constantly exploring innovative techniques to reduce our environmental impact while maintaining productivity.\n\nFrom solar-powered irrigation systems to water conservation techniques, we're committed to reducing our footprint. We've also implemented integrated pest management, which uses natural predators and biological controls to protect our crops without harmful chemicals.\n\nOur vision is to demonstrate that sustainable agriculture can be economically viable while preserving the planet. By sharing our practices and knowledge, we hope to inspire other farmers to join the sustainable agriculture movement.",
      imageField: "story3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/720582fd-a54c-491b-97b3-8ae85b181ada_Sustainable_Agriculture.jpeg",
      categoryKey: "story3_category",
      category: "Sustainability",
      authorKey: "story3_author",
      author: "Dr. Sarah Chen",
      dateKey: "story3_date",
      date: "January 5, 2025",
      readTimeKey: "story3_readTime",
      readTime: "6 min read"
    }
  ];

  // Farm Statistics - Added editable fields
  const farmStats = [
    { numberKey: "stat1_number", number: "50+", labelKey: "stat1_label", label: "Organic Varieties" },
    { numberKey: "stat2_number", number: "200+", labelKey: "stat2_label", label: "Happy Customers" },
    { numberKey: "stat3_number", number: "15+", labelKey: "stat3_label", label: "Years of Farming" },
    { numberKey: "stat4_number", number: "100%", labelKey: "stat4_label", label: "Satisfaction" },
  ];

  // Category options - With editable labels
  const categoryOptions = [
    { key: "All", labelKey: "category_all", label: "All" },
    { key: "Vegetables", labelKey: "category_vegetables", label: "Vegetables" },
    { key: "Leafy", labelKey: "category_leafy", label: "Leafy" },
    { key: "Honey", labelKey: "category_honey", label: "Honey" },
    { key: "Dairy", labelKey: "category_dairy", label: "Dairy" },
    { key: "Poultry", labelKey: "category_poultry", label: "Poultry" },
    { key: "Grains", labelKey: "category_grains", label: "Grains" },
    { key: "Garden", labelKey: "category_garden", label: "Garden" },
  ];

  // Filter products by category
  const getFilteredProducts = () => {
    if (activeCategory === "All") {
      return editableFarmProducts;
    }
    return editableFarmProducts.filter(product => product.category === activeCategory);
  };


  // Top bar messages
  const topBarMessages = [
    { key: "top_bar_message_1", value: "🌾 FRESH FROM OUR FARM DAILY" },
    { key: "top_bar_message_2", value: "✦ ORGANIC & SUSTAINABLE" },
    { key: "top_bar_message_3", value: "🌱 FARM TO TABLE PRODUCE" },
    { key: "top_bar_message_4", value: "✦ FREE DELIVERY ON ORDERS OVER ₹500" },
  ];

  // Floating particles
  const particles = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 6,
    delay: Math.random() * 6,
    duration: 6 + Math.random() * 8,
    opacity: 0.05 + Math.random() * 0.12,
    rotation: Math.random() * 360,
    type: Math.random() > 0.5 ? 'leaf' : 'sparkle'
  }));

  // Floating clouds
  const clouds = Array.from({ length: 5 }, (_, i) => ({
    id: i,
    left: -10 + Math.random() * 70,
    top: 5 + Math.random() * 35,
    width: 120 + Math.random() * 200,
    height: 30 + Math.random() * 50,
    delay: i * 2.5,
    duration: 18 + Math.random() * 15,
    opacity: 0.08 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % farmHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate items on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            const index = parseInt(target.dataset.index);
            if (!isNaN(index) && !visibleItems.includes(index)) {
              setTimeout(() => {
                setVisibleItems(prev => {
                  if (!prev.includes(index)) {
                    return [...prev, index];
                  }
                  return prev;
                });
              }, index * 100);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => {
      observer.observe(el);
    });

    setTimeout(() => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          const index = parseInt(el.dataset.index);
          if (!isNaN(index) && !visibleItems.includes(index)) {
            setVisibleItems(prev => {
              if (!prev.includes(index)) {
                return [...prev, index];
              }
              return prev;
            });
          }
        }
      });
    }, 100);

    return () => observer.disconnect();
  }, []);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler for navigation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'products', 'features', 'stories'];
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(`section-${section}`);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  }, [p.previewEditMode, p.activeEditField]);

  // Story Modal Functions
  const openStoryModal = useCallback((story, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedStory(story);
  }, [p.previewEditMode, p.activeEditField]);

  const closeStoryModal = useCallback(() => {
    setSelectedStory(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedStory(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || farmHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = farmHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, farmHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "FARM FRESH",
    title: p.storeTheme?.heroTitle || "Nourishing the Land, Feeding the Soul",
    subtitle: p.storeTheme?.heroSubtitle || "Sustainable farming for a healthier tomorrow",
    primaryCta: p.storeTheme?.primaryCtaText || "Explore Our Farm",
    secondaryCta: p.storeTheme?.secondaryCtaText || "Learn More",
  };

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", section: "home" },
    { key: "nav_about", label: "ABOUT", section: "about" },
    { key: "nav_products", label: "PRODUCTS", section: "products" },
    { key: "nav_features", label: "FEATURES", section: "features" },
    { key: "nav_stories", label: "STORIES", section: "stories" },
  ];

  // Get editable products with imageField
  const editableFarmProducts = farmProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable stories with imageField
  const editableFarmStories = farmStories.map((story) => ({
    ...story,
    image: p.storeTheme?.[story.imageField] || story.image,
  }));
  const filteredProducts = getFilteredProducts();

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-teal-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'DM Sans', sans-serif" }}>
      
      {/* Nature Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute ${particle.type === 'leaf' ? 'leaf-float' : 'sparkle-float'}`}
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: particle.type === 'leaf' 
                ? `radial-gradient(circle, ${primary}${Math.floor(30 + Math.random() * 30).toString(16)}, transparent)` 
                : `radial-gradient(circle, ${secondary}${Math.floor(30 + Math.random() * 30).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
              transform: `rotate(${particle.rotation}deg)`,
            }}
          />
        ))}
      </div>

      {/* Floating Clouds */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {clouds.map((cloud) => (
          <div
            key={cloud.id}
            className="absolute cloud-float"
            style={{
              left: `${cloud.left}%`,
              top: `${cloud.top}%`,
              width: `${cloud.width}px`,
              height: `${cloud.height}px`,
              animationDelay: `${cloud.delay}s`,
              animationDuration: `${cloud.duration}s`,
              opacity: cloud.opacity,
            }}
          >
            <div className="w-full h-full rounded-full bg-white/20 blur-3xl" />
          </div>
        ))}
      </div>
      
      {/* Top Bar - All messages editable */}
      <div className="relative bg-teal-900 text-teal-200 py-2.5 overflow-hidden border-b-2 border-teal-700/30">
        <div className="flex items-center justify-center gap-6 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg) => (
            <span key={msg.key} className="inline-block mx-5 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={msg.key} 
                value={msg.value}
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - All nav labels editable */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-teal-900/95 backdrop-blur-xl shadow-lg py-2 border-b border-teal-700/20" : "bg-teal-900/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => scrollToSection("home", e)}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-xl border-2 border-teal-400/50 shadow-lg shadow-teal-700/20">
              🌾
            </div>
            <span className="font-bold text-xl tracking-tight text-teal-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Farm Fresh"} 
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-teal-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-5">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => scrollToSection(item.section, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activeSection === item.section 
                    ? "text-teal-300" 
                    : "text-teal-100/80 hover:text-teal-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.section}`} 
                  value={item.label}
                  onNavigate={() => {}}
                  navPage="home"
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-teal-400 transition-all duration-300 ${activeSection === item.section ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-teal-800/50">
                <FaHeart className="text-lg text-teal-100/80 hover:text-teal-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-teal-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-teal-900 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-teal-800/30">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => {
                  scrollToSection(item.section, e);
                  setMobileMenuOpen(false);
                }}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activeSection === item.section 
                    ? "bg-teal-800/30 text-teal-300" 
                    : "text-teal-100/80 hover:text-teal-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.section}`} 
                  value={item.label}
                  onNavigate={() => {}}
                  navPage="home"
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== SECTION: HOME ==================== */}
      <section id="section-home" className="relative w-full min-h-screen overflow-hidden -mt-[60px]">
        <div className="absolute inset-0 w-full h-full">
          {farmHeroImageConfigs.map((img, idx) => (
            <div
              key={img.field}
              className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                <img 
                  src={p.storeTheme?.[img.field] || img.src} 
                  alt={`Farm ${idx + 1}`} 
                  className="w-full h-full object-cover object-center" 
                  style={{ 
                    transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                    transition: "transform 0.3s ease-out" 
                  }} 
                />
              </InlineEditableImage>
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 via-teal-900/40 to-transparent z-20 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-teal-50 via-transparent to-transparent z-20 pointer-events-none" />
          
          <div className="absolute bottom-0 left-0 right-0 z-20">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#F0FDFA" opacity="0.95" />
            </svg>
          </div>
        </div>
        
        <div className="relative z-30 h-full min-h-screen w-full px-4 md:px-8 flex items-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 animate-bounce-in">
              <div className="flex items-center gap-3 px-5 py-2.5 bg-teal-800/40 backdrop-blur-md rounded-full border border-teal-400/30">
                <span className="w-2 h-2 rounded-full bg-teal-400 animate-ping" />
                <span className="text-teal-200 text-xs tracking-[0.2em] uppercase">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroEyebrow" 
                    value={copy.eyebrow}
                    onNavigate={() => {}}
                    navPage="home"
                  />
                </span>
                <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
              <InlineEditableTextWithNav 
                p={p} 
                field="heroTitle" 
                value={copy.title}
                onNavigate={() => {}}
                navPage="home"
              >
                {copy.title.split(" ").map((word, i) => (
                  <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                ))}
              </InlineEditableTextWithNav>
            </h1>
            <p className="mt-6 text-teal-100/90 text-lg max-w-xl animate-slide-right">
              <InlineEditableTextWithNav 
                p={p} 
                field="heroSubtitle" 
                value={copy.subtitle}
                onNavigate={() => {}}
                navPage="home"
              />
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button 
                onClick={(e) => scrollToSection("products", e)} 
                className="group px-8 py-4 rounded-full bg-teal-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-teal-600/30 flex items-center gap-2 animate-slide-up"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="primaryCtaText" 
                  value={copy.primaryCta}
                  onNavigate={() => {}}
                  navPage="products"
                />
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
              <button 
                onClick={(e) => scrollToSection("about", e)} 
                className="px-8 py-4 rounded-full border-2 border-teal-400/50 text-teal-200 font-semibold tracking-wide transition-all duration-300 hover:bg-teal-600/30 hover:border-teal-300 backdrop-blur-sm animate-slide-up"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="secondaryCtaText" 
                  value={copy.secondaryCta}
                  onNavigate={() => {}}
                  navPage="about"
                />
              </button>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
          <div className="flex flex-col items-center gap-1">
            <span className="text-teal-200/60 text-[10px] tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field="scroll_text" 
                value="SCROLL TO EXPLORE"
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
            <div className="w-5 h-8 border-2 border-teal-400/30 rounded-full flex justify-center">
              <div className="w-1 h-2 bg-teal-400/60 rounded-full mt-1 animate-scroll-dot" />
            </div>
          </div>
        </div>
      </section>
      
      {/* ==================== SECTION: ABOUT ==================== */}
      <section id="section-about" className="w-full px-4 md:px-8 py-20 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="about_badge" 
                value="About Our Farm"
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-teal-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="about_title" 
                value="Where Good Food Begins"
                onNavigate={() => {}}
                navPage="home"
              />
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-teal-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll" data-index="0">
              <p className="text-gray-600 leading-relaxed text-lg mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Farm Fresh is a family-owned sustainable farm dedicated to producing high-quality organic food while nurturing the land for future generations."
                  onNavigate={() => {}}
                  navPage="home"
                />
              </p>
              <p className="text-gray-600 leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="We believe in transparency, quality, and community. Every product from our farm tells a story of care, sustainability, and dedication to the craft of farming."
                  onNavigate={() => {}}
                  navPage="home"
                />
              </p>
              
              <div className="grid grid-cols-2 gap-4 mt-8">
                {farmStats.map((stat, idx) => (
                  <div key={idx} className="bg-teal-50 rounded-2xl p-4 text-center border border-teal-200 animate-on-scroll" data-index={idx + 1}>
                    <p className="text-3xl font-bold text-teal-700">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={stat.numberKey} 
                        value={stat.number}
                        onNavigate={() => {}}
                        navPage="home"
                      />
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={stat.labelKey} 
                        value={stat.label}
                        onNavigate={() => {}}
                        navPage="home"
                      />
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-teal-200/50 animate-on-scroll" data-index="5">
              <InlineEditableImage p={p} field="farmAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.farmAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/698aeaf4-8dfb-45b8-b7de-488a99d8331c_farm1.jpg"} 
                  alt="About Farm" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </div>
      </section>
      
      {/* ==================== SECTION: PRODUCTS ==================== */}
      <section id="section-products" className="w-full px-4 md:px-8 py-20 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_badge" 
                value="Our Harvest"
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-teal-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_title" 
                value="Fresh from the Farm"
                onNavigate={() => {}}
                navPage="home"
              />
            </h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_subtitle" 
                value="Handpicked, fresh, and delivered with care"
                onNavigate={() => {}}
                navPage="home"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-teal-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categoryOptions.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-teal-600 text-white shadow-lg" 
                    : "bg-white border border-teal-200 text-teal-700 hover:bg-teal-50"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={cat.labelKey} 
                  value={cat.label}
                  onNavigate={() => {}}
                  navPage="home"
                />
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {filteredProducts.map((product, idx) => {
              const totalIndex = idx + 10;
              return (
                <div 
                  key={product.id} 
                  className="group cursor-pointer animate-on-scroll" 
                  data-index={totalIndex}
                  onClick={() => setSelectedProduct(product)}
                  style={{ 
                    opacity: visibleItems.includes(totalIndex) ? 1 : 0,
                    transform: visibleItems.includes(totalIndex) ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'all 0.6s cubic-bezier(0.34, 1.2, 0.64, 1)',
                    transitionDelay: `${idx * 0.05}s`
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-teal-200 hover:border-teal-400">
                    <div className="relative h-48 overflow-hidden bg-teal-50">
                      <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </InlineEditableImage>
                      {product.badge && (
                        <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.badgeKey} 
                            value={product.badge}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-teal-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.categoryKey} 
                            value={product.category}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                      </div>
                      <h3 className="font-semibold text-teal-900 text-sm">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={product.nameKey} 
                          value={product.name}
                          onNavigate={() => {}}
                          navPage="home"
                        />
                      </h3>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-lg font-bold text-teal-700">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.priceKey} 
                            value={product.price}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="no_products_message" 
                  value="No products found in this category"
                  onNavigate={() => {}}
                  navPage="home"
                />
              </p>
            </div>
          )}
        </div>
      </section>
      
      {/* ==================== SECTION: FEATURES ==================== */}
      <section id="section-features" className="w-full px-4 md:px-8 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="features_badge" 
                value="Why Choose Us"
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-teal-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="features_title" 
                value="Our Promise to You"
                onNavigate={() => {}}
                navPage="home"
              />
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-teal-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {farmFeatures.map((feature, idx) => {
              const totalIndex = idx + 50;
              return (
                <div 
                  key={idx} 
                  className="group animate-on-scroll" 
                  data-index={totalIndex}
                  style={{ 
                    opacity: visibleItems.includes(totalIndex) ? 1 : 0,
                    transform: visibleItems.includes(totalIndex) ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'all 0.6s cubic-bezier(0.34, 1.2, 0.64, 1)',
                    transitionDelay: `${idx * 0.08}s`
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl bg-teal-50 border border-teal-200 hover:border-teal-400">
                    <div className="text-5xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-teal-900 text-base">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={feature.titleKey} 
                        value={feature.title}
                        onNavigate={() => {}}
                        navPage="home"
                      />
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={feature.descKey} 
                        value={feature.desc}
                        onNavigate={() => {}}
                        navPage="home"
                      />
                    </p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-600 to-teal-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* ==================== SECTION: STORIES ==================== */}
      <section id="section-stories" className="w-full px-4 md:px-8 py-20 bg-gradient-to-b from-white to-teal-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="stories_badge" 
                value="Farm Stories"
                onNavigate={() => {}}
                navPage="home"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-teal-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="stories_title" 
                value="From Our Fields"
                onNavigate={() => {}}
                navPage="home"
              />
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-teal-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableFarmStories.map((story, idx) => {
              const totalIndex = idx + 80;
              return (
                <div 
                  key={story.id} 
                  className="group animate-on-scroll cursor-pointer" 
                  data-index={totalIndex}
                  onClick={(e) => openStoryModal(story, e)}
                  style={{ 
                    opacity: visibleItems.includes(totalIndex) ? 1 : 0,
                    transform: visibleItems.includes(totalIndex) ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'all 0.6s cubic-bezier(0.34, 1.2, 0.64, 1)',
                    transitionDelay: `${idx * 0.1}s`
                  }}
                >
                  <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-teal-200 hover:border-teal-400">
                    <div className="h-48 overflow-hidden">
                      <InlineEditableImage p={p} field={story.imageField} className="block h-full w-full overflow-hidden">
                        <img src={story.image} alt={story.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </InlineEditableImage>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-semibold">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={story.categoryKey} 
                            value={story.category}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                        <span className="text-xs text-gray-400">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={story.readTimeKey} 
                            value={story.readTime}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-teal-900 mb-2">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={story.titleKey} 
                          value={story.title}
                          onNavigate={() => {}}
                          navPage="home"
                        />
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={story.excerptKey} 
                          value={story.excerpt}
                          onNavigate={() => {}}
                          navPage="home"
                        />
                      </p>
                      <div className="mt-4 text-teal-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        <span className="flex items-center gap-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field="read_full_story" 
                            value="Read Full Story"
                            onNavigate={() => {}}
                            navPage="home"
                          />
                          <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-gray-400 flex items-center gap-3">
                        <span>✍️ 
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={story.authorKey} 
                            value={story.author}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                        <span>📅 
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={story.dateKey} 
                            value={story.date}
                            onNavigate={() => {}}
                            navPage="home"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* ==================== FOOTER ==================== */}
      <section className="bg-teal-900 text-teal-200 py-16 w-full">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🌾</span>
                <span className="font-bold text-xl text-teal-100">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="footer_brand" 
                    value="Farm Fresh"
                    onNavigate={() => {}}
                    navPage="home"
                  />
                </span>
              </div>
              <p className="text-sm text-teal-300/80">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="footer_tagline" 
                  value="Nourishing the land, feeding the soul"
                  onNavigate={() => {}}
                  navPage="home"
                />
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-teal-100 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="footer_quick_links" 
                  value="Quick Links"
                  onNavigate={() => {}}
                  navPage="home"
                />
              </h4>
              <ul className="space-y-2 text-sm">
                {["About", "Products", "Features", "Stories"].map((item) => (
                  <li key={item}>
                    <button 
                      onClick={(e) => scrollToSection(item.toLowerCase(), e)}
                      className="text-teal-300/80 hover:text-teal-100 transition-colors"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`footer_link_${item.toLowerCase()}`} 
                        value={item}
                        onNavigate={() => {}}
                        navPage="home"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-teal-100 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="footer_contact" 
                  value="Contact"
                  onNavigate={() => {}}
                  navPage="home"
                />
              </h4>
              <ul className="space-y-2 text-sm text-teal-300/80">
                <li>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="footer_phone" 
                    value="📞 +91 98765 43210"
                    onNavigate={() => {}}
                    navPage="home"
                  />
                </li>
                <li>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="footer_email" 
                    value="✉️ hello@farmfresh.com"
                    onNavigate={() => {}}
                    navPage="home"
                  />
                </li>
                <li>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="footer_address" 
                    value="📍 Farm Road, India"
                    onNavigate={() => {}}
                    navPage="home"
                  />
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-teal-100 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="footer_follow" 
                  value="Follow Us"
                  onNavigate={() => {}}
                  navPage="home"
                />
              </h4>
              <div className="flex gap-3 text-xl">
                <span className="cursor-pointer hover:text-teal-100 transition-colors">🌿</span>
                <span className="cursor-pointer hover:text-teal-100 transition-colors">📸</span>
                <span className="cursor-pointer hover:text-teal-100 transition-colors">🐦</span>
                <span className="cursor-pointer hover:text-teal-100 transition-colors">📺</span>
              </div>
            </div>
          </div>
          <div className="border-t border-teal-800/30 mt-8 pt-8 text-center text-sm text-teal-400/60">
            <InlineEditableTextWithNav 
              p={p} 
              field="footer_copyright" 
              value="© 2024 Farm Fresh. All rights reserved."
              onNavigate={() => {}}
              navPage="home"
            />
          </div>
        </div>
      </section>
      
      {/* ==================== PRODUCT MODAL - REMOVED ADD TO CART ==================== */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-3xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-teal-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.badgeKey} 
                    value={selectedProduct.badge}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.categoryKey} 
                    value={selectedProduct.category}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </span>
              </div>
              <h3 className="text-2xl font-bold text-teal-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={() => {}}
                  navPage="home"
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_description" 
                  value={`Fresh from our farm, this premium ${selectedProduct.name.toLowerCase()} is grown with care and harvested at peak ripeness.`}
                  onNavigate={() => {}}
                  navPage="home"
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-teal-700">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </span>
              </div>
              <div className="mt-6 text-sm text-teal-600 text-center bg-teal-50 p-3 rounded-full border border-teal-200">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_message" 
                  value="🌾 Fresh from our farm to your table"
                  onNavigate={() => {}}
                  navPage="home"
                  isInModal={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ==================== STORY MODAL ==================== */}
      {selectedStory && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-5 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedStory.titleKey} 
                    value={selectedStory.title}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </h3>
                <div className="flex items-center gap-3 text-teal-200 text-xs mt-1">
                  <span>✍️ 
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedStory.authorKey} 
                      value={selectedStory.author}
                      onNavigate={() => {}}
                      navPage="home"
                      isInModal={true}
                    />
                  </span>
                  <span>•</span>
                  <span>📅 
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedStory.dateKey} 
                      value={selectedStory.date}
                      onNavigate={() => {}}
                      navPage="home"
                      isInModal={true}
                    />
                  </span>
                  <span>•</span>
                  <span>📖 
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedStory.readTimeKey} 
                      value={selectedStory.readTime}
                      onNavigate={() => {}}
                      navPage="home"
                      isInModal={true}
                    />
                  </span>
                </div>
              </div>
              <button 
                onClick={closeStoryModal} 
                className="w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/30 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6">
              <div className="mb-6 rounded-2xl overflow-hidden">
                <InlineEditableImage p={p} field={selectedStory.imageField} className="block w-full overflow-hidden">
                  <img src={selectedStory.image} alt={selectedStory.title} className="w-full h-72 object-cover" />
                </InlineEditableImage>
              </div>
              
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs rounded-full font-semibold">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedStory.categoryKey} 
                    value={selectedStory.category}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </span>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-base">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedStory.fullStoryKey} 
                    value={selectedStory.fullStory}
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </div>
              </div>
              
              <div className="mt-6 flex items-center gap-4 text-sm text-gray-400 border-t border-teal-100 pt-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="story_likes_label" 
                    value="🌱 Organic Farming"
                    onNavigate={() => {}}
                    navPage="home"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>❤️ {Math.floor(Math.random() * 200 + 50)} likes</span>
                <span>•</span>
                <span>💬 {Math.floor(Math.random() * 30 + 5)} comments</span>
              </div>
              
              <button 
                onClick={closeStoryModal} 
                className="mt-4 w-full py-3 rounded-full bg-teal-600 text-white font-semibold hover:bg-teal-700 transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_story_button" 
                  value="Close Story 🌾"
                  onNavigate={() => {}}
                  navPage="home"
                  isInModal={true}
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes leaf-float { 
          0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0.05; }
          25% { transform: translate(15px, -25px) rotate(90deg) scale(1.2); opacity: 0.15; }
          50% { transform: translate(-10px, -50px) rotate(180deg) scale(0.8); opacity: 0.1; }
          75% { transform: translate(15px, -35px) rotate(270deg) scale(1.1); opacity: 0.15; }
          100% { transform: translate(0, -70px) rotate(360deg) scale(1); opacity: 0.05; }
        }
        .leaf-float { animation: leaf-float 7s ease-in-out infinite; }
        
        @keyframes sparkle-float {
          0% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1.5) rotate(180deg); opacity: 0.2; }
          100% { transform: scale(0) rotate(360deg); opacity: 0; }
        }
        .sparkle-float { animation: sparkle-float 5s ease-in-out infinite; border-radius: 50%; }
        
        @keyframes cloud-float {
          0% { transform: translateX(0px); }
          50% { transform: translateX(80px); }
          100% { transform: translateX(0px); }
        }
        .cloud-float { animation: cloud-float 22s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}